import React, { useState, useEffect } from 'react';
import { documentsAPI } from '../services/api';
import { FiFile, FiDownload, FiTrash2, FiUpload, FiX, FiCheck, FiShield } from 'react-icons/fi';
import { ConfirmModal, useConfirm } from './shared/Feedback';
import './StudentDocuments.css';

// Source badge: shows where the document came from
const SourceBadge = ({ doc }) => {
  if (!doc.uploaded_by) {
    return <span style={{ fontSize: '11px', background: '#e3f2fd', color: '#1565c0', borderRadius: '4px', padding: '2px 6px', marginLeft: '6px' }}>🌐 Website</span>;
  }
  // Determine if the uploader is the student's own account (uploaded_by username matches student)
  const uploaderUsername = doc.uploaded_by_username || doc.uploaded_by?.username || '';
  if (uploaderUsername && (uploaderUsername.startsWith('student_') || uploaderUsername.startsWith('STU'))) {
    return <span style={{ fontSize: '11px', background: '#f3e5f5', color: '#6a1b9a', borderRadius: '4px', padding: '2px 6px', marginLeft: '6px' }}>📱 Student App</span>;
  }
  return <span style={{ fontSize: '11px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', padding: '2px 6px', marginLeft: '6px' }}>👤 {uploaderUsername || 'Staff'}</span>;
};

// Verification badge
const VerificationBadge = ({ doc }) => {
  if (doc.verified_at) {
    return (
      <span style={{ fontSize: '11px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', padding: '2px 6px', marginLeft: '4px' }}>
        ✅ Verified{doc.verified_by_name ? ` by ${doc.verified_by_name}` : ''}
      </span>
    );
  }
  return (
    <span style={{ fontSize: '11px', background: '#f5f5f5', color: '#757575', borderRadius: '4px', padding: '2px 6px', marginLeft: '4px' }}>
      ⏳ Pending verification
    </span>
  );
};

const StudentDocuments = ({ studentId, canEdit = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const { confirmState, requestConfirm, confirm, cancel } = useConfirm();
  const [uploadData, setUploadData] = useState({
    document_type: '',
    file: null
  });

  useEffect(() => {
    if (studentId) {
      fetchDocuments();
    }
  }, [studentId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await documentsAPI.byStudent(studentId);
      setDocuments(response.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }

      // Validate file type based on document type
      if (uploadData.document_type === 'PHOTO') {
        if (!file.type.startsWith('image/')) {
          setError('Photo must be an image file (JPG/PNG)');
          return;
        }
      } else if (uploadData.document_type === 'CERTIFICATE') {
        const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!allowed.includes(file.type)) {
          setError('Certificate must be PDF or JPG/PNG');
          return;
        }
      } else {
        if (file.type !== 'application/pdf') {
          setError('Passport and Transcript must be PDF files');
          return;
        }
      }

      setUploadData({ ...uploadData, file });
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.document_type || !uploadData.file) {
      setError('Please select document type and file');
      return;
    }

    try {
      setUploading(true);
      setError('');
      
      await documentsAPI.create({
        student: studentId,
        document_type: uploadData.document_type,
        file: uploadData.file
      });

      // Reset form and refresh documents
      setUploadData({ document_type: '', file: null });
      setShowUploadForm(false);
      fetchDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleVerify = async (documentId) => {
    try {
      setVerifyingId(documentId);
      setError('');
      await documentsAPI.verify(documentId, '');
      fetchDocuments();
    } catch (err) {
      console.error('Error verifying document:', err);
      setError('Failed to verify document');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDelete = (documentId) => {
    requestConfirm(
      { title: 'Delete Document', message: 'Delete this document? This cannot be undone.' },
      async () => {
        try {
          await documentsAPI.delete(documentId);
          fetchDocuments();
        } catch (err) {
          console.error('Error deleting document:', err);
          setError('Failed to delete document');
        }
      }
    );
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `document-${doc.document_type}-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(err.response?.status === 404 ? 'File not found' : 'Failed to download document');
    }
  };

  const getDocumentIcon = (documentType) => {
    switch (documentType) {
      case 'PASSPORT':
        return '📘';
      case 'TRANSCRIPT':
        return '📄';
      case 'PHOTO':
        return '📷';
      case 'CERTIFICATE':
        return '🎓';
      default:
        return '📎';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (!studentId) {
    return null;
  }

  return (
    <div className="student-documents">
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger !== false}
          loading={confirmState.loading}
          onConfirm={confirm}
          onCancel={cancel}
        />
      )}
      <div className="documents-header">
        <h3>Documents</h3>
        {canEdit && (
          <button
            type="button"
            className="btn-upload"
            onClick={() => setShowUploadForm(!showUploadForm)}
          >
            <FiUpload /> {showUploadForm ? 'Cancel' : 'Upload Document'}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showUploadForm && canEdit && (
        <div className="upload-form">
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label>Document Type *</label>
              <select
                value={uploadData.document_type}
                onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                required
              >
                <option value="">Select document type</option>
                <option value="PASSPORT">Passport</option>
                <option value="TRANSCRIPT">Transcript</option>
                <option value="PHOTO">Photo</option>
                <option value="CERTIFICATE">Certificate</option>
              </select>
            </div>

            <div className="form-group">
              <label>File *</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept={
                  uploadData.document_type === 'PHOTO'
                    ? 'image/jpeg,image/png'
                    : uploadData.document_type === 'CERTIFICATE'
                    ? 'application/pdf,image/jpeg,image/png'
                    : 'application/pdf'
                }
                required
              />
              {uploadData.file && (
                <div className="file-info">
                  <FiCheck className="check-icon" />
                  {uploadData.file.name} ({formatFileSize(uploadData.file.size)})
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" disabled={uploading} className="btn-primary">
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadData({ document_type: '', file: null });
                  setError('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="no-documents">
          <FiFile className="empty-icon" />
          <p>No documents uploaded yet</p>
        </div>
      ) : (
        <div className="documents-list">
          {documents.map((doc) => (
            <div key={doc.id} className="document-item">
              <div className="document-info">
                <span className="document-icon">{getDocumentIcon(doc.document_type)}</span>
                <div className="document-details">
                  <div className="document-name">
                    {doc.document_type_display}
                    <SourceBadge doc={doc} />
                    <VerificationBadge doc={doc} />
                  </div>
                  <div className="document-meta">
                    {doc.file_name} • {formatFileSize(doc.file_size)}
                    {doc.uploaded_at && (
                      <span> • {new Date(doc.uploaded_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="document-actions">
                <button
                  type="button"
                  className="btn-download"
                  onClick={() => handleDownload(doc)}
                  title="Download document"
                >
                  <FiDownload className="btn-download-icon" aria-hidden />
                </button>
                {canEdit && !doc.verified_at && (
                  <button
                    type="button"
                    className="btn-verify"
                    onClick={() => handleVerify(doc.id)}
                    disabled={verifyingId === doc.id}
                    title="Mark as verified"
                    style={{ background: 'none', border: '1px solid #43a047', color: '#43a047', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    <FiShield style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                    {verifyingId === doc.id ? '...' : 'Verify'}
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={() => handleDelete(doc.id)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentDocuments;

